import React from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import i18n from 'i18n-js';

import OperatorPanelInformationJobsScreen from './components/OperatorPanelInformationJobsScreen';
import OperatorPanelInformationNcFilesScreen from './components/OperatorPanelInformationNcFilesScreen';
import OperatorPanelInformationSetupScreen from './components/OperatorPanelInformationSetupScreen';
import OperatorPanelInformationToolsScreen from './components/OperatorPanelInformationToolsScreen';
import OperatorPanelInformationNotesScreen from './components/OperatorPanelInformationNotesScreen';
import OperatorPanelInformation2DDrawingScreen from './components/OperatorPanelInformation2DDrawingScreen';
import OperatorPanelInformation3DDrawingScreen from './components/OperatorPanelInformation3DDrawingScreen';
import OperatorPanelInformationClampingScreen from './components/OperatorPanelInformationClampingScreen';
import OperatorPanelInformationPartScreen from './components/OperatorPanelInformationPartScreen';
import OperatorPanelInformationMaterialScreen from './components/OperatorPanelInformationMaterialScreen';
import OperatorPanelInformationProductionOrderScreen from './components/OperatorPanelInformationProductionOrderScreen';
import OperatorPanelInformationMeasurementScreen from './components/OperatorPanelInformationMeasurementScreen';
import OperatorPanelInformationAllDocumentsScreen from './components/OperatorPanelInformationAllDocumentsScreen/OperatorPanelInformationAllDocumentsScreen';
import OperatorPanelInformationSearchNCFilesScreen from './components/OperatorPanelInformationSearchNCFilesScreen';
import OperatorPanelInformationSearchHistoryScreen from './components/OperatorPanelInformationSearchHistoryScreen';

import { NAVIGATION_CONFIG } from '@v2/pages/common/constants/base.constants';
import { ColorFunction } from '@styles/colors/types';
import { OPERATOR_PANEL_INFORMATION_SCREENS, scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { connect, useSelector } from 'react-redux';
import { useRoute } from '@react-navigation/native';
import { selectSelectedItem } from '@reducers/machines';
import { Machine } from '@interfaces/machines';
import { buildOPTabRequirementsContext } from '@components/operatorPanel/helpers/buildOPTabRequirementsContext';
import { getInitialOPInformationScreen } from '@components/operatorPanel/helpers/getInitialOPInformationScreen';

const routes = {
  name: OPERATOR_PANEL_INFORMATION_SCREENS.Information,
  nested: [
    { name: OPERATOR_PANEL_INFORMATION_SCREENS.Jobs, Component: OperatorPanelInformationJobsScreen, title: i18n.t('Job') },
    { name: OPERATOR_PANEL_INFORMATION_SCREENS.NcFiles, Component: OperatorPanelInformationNcFilesScreen, title: i18n.t('NC File') },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.Setup,
      Component: OperatorPanelInformationSetupScreen,
      title: i18n.t('Setup Sheet'),
    },
    { name: OPERATOR_PANEL_INFORMATION_SCREENS.Tools, Component: OperatorPanelInformationToolsScreen, title: i18n.t('Tools') },
    { name: OPERATOR_PANEL_INFORMATION_SCREENS.Notes, Component: OperatorPanelInformationNotesScreen, title: i18n.t('Notes') },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS['2DDrawing'],
      Component: OperatorPanelInformation2DDrawingScreen,
      title: i18n.t('2D Drawing'),
    },
    { name: OPERATOR_PANEL_INFORMATION_SCREENS['3DDrawing'], Component: OperatorPanelInformation3DDrawingScreen, title: i18n.t('3D Drawing') },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.Clamping,
      Component: OperatorPanelInformationClampingScreen,
      title: i18n.t('Clamping Images'),
    },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.Part,
      Component: OperatorPanelInformationPartScreen,
      title: i18n.t('Part Image'),
    },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.Material,
      Component: OperatorPanelInformationMaterialScreen,
      title: i18n.t('Material Image'),
    },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.ProductionOrder,
      Component: OperatorPanelInformationProductionOrderScreen,
      title: i18n.t('Production Order'),
    },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.Measurement,
      Component: OperatorPanelInformationMeasurementScreen,
      title: i18n.t('Measure Document'),
    },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments,
      Component: OperatorPanelInformationAllDocumentsScreen,
      title: i18n.t('All Documents'),
    },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.SearchNCFiles,
      Component: OperatorPanelInformationSearchNCFilesScreen,
      title: i18n.t('NC Files'),
    },
    {
      name: OPERATOR_PANEL_INFORMATION_SCREENS.SearchHistory,
      Component: OperatorPanelInformationSearchHistoryScreen,
      title: i18n.t('NC File History'),
    },
  ],
};

const StackNavigator = createStackNavigator();

interface OperatorPanelInformationNavigationProps {
  C: ColorFunction;
  isWithERP: boolean | null;
}

const OperatorPanelInformationNavigation: React.FC<OperatorPanelInformationNavigationProps> = (props) => {
  const { C, isWithERP } = props;

  const route = useRoute();
  const selectedItem: Machine = useSelector(selectSelectedItem);

  const initialRouteName = getInitialOPInformationScreen(
    buildOPTabRequirementsContext({
      isWithERP,
      isMachinePerspective: route.params?.[scheduledProductionOperationIdParam] == null,
      ncFileStatus: selectedItem?.machineStatus?.ncFileStatus,
    })
  );

  return (
    <StackNavigator.Navigator
      screenOptions={{
        ...NAVIGATION_CONFIG,
        cardStyle: {
          backgroundColor: C('Background/Light/Detail'),
        },
      }}
      initialRouteName={initialRouteName}
    >
      {routes.nested.map((item) => {
        return (
          <StackNavigator.Screen key={item.name} name={item.name} options={{ ...NAVIGATION_CONFIG, title: item.title, unmountOnBlur: true }}>
            {(p) => {
              // Create a unique key based on the route params to force remount
              const routeKey = item.name === OPERATOR_PANEL_INFORMATION_SCREENS.Jobs
                ? p.route?.params?.scheduledProductionOperationId
                  ? `job-${p.route.params.scheduledProductionOperationId}`
                  : 'machine'
                : item.name;
              return <item.Component key={routeKey} {...p} {...props} />;
            }}
          </StackNavigator.Screen>
        );
      })}
    </StackNavigator.Navigator>
  );
};

export default connect(({ membership: { isWithERP } }) => ({ isWithERP }), {})(OperatorPanelInformationNavigation);
